// client/src/dnd/emitter_tool/components/panels/TargetingPanel.js
import React, { useEffect, useRef, useState } from "react";

const OPEN_KEY = "EmitterTool:TargetingPanelOpenV1";

const label = { fontSize: 12, opacity: 0.9 };
const numInputStyle = {
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 6,
  padding: "6px 8px",
  fontSize: 12,
  boxSizing: "border-box",
  width: 80,
};
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const emit = (name, detail) => {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {}
};

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(OPEN_KEY);
      return v == null ? defaultOpen : v === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          gap: 8,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </span>
        <span style={{ opacity: 0.75 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

const TARGET_PRESETS = [
  {
    id: "global",
    label: "Global (Ambient / Weather)",
    spell: { trigger: { type: "AutoGlobalAmbient" }, targeting: { mode: "Global" } },
  },
  {
    id: "point",
    label: "Origin → Target Point",
    spell: { trigger: { type: "ClickTargetPoint" }, targeting: { mode: "Point" } },
  },
  {
    id: "tokentotarget",
    label: "Token → Target (launch)",
    spell: { trigger: { type: "ClickTokenToTarget" }, targeting: { mode: "TokenToTarget" } },
  },
  {
    id: "direction",
    label: "Origin + Direction (cone/line)",
    spell: {
      trigger: { type: "ClickDirection" },
      targeting: { mode: "Direction", angleDeg: 60, length: 3 },
    },
  },
];

const byId = Object.fromEntries(TARGET_PRESETS.map((p) => [p.id, p]));

function normalizeModeId(m) {
  const s = (m || "").toString().toLowerCase().trim();
  if (!s) return null;
  if (s === "global") return "global";
  if (s === "point") return "point";
  if (s === "direction") return "direction";
  if (
    s === "tokentotarget" ||
    s === "token_to_target" ||
    s === "tokenlaunch" ||
    s === "token-launch"
  ) {
    return "tokentotarget";
  }
  if (byId[s]) return s;
  return null;
}

export default function TargetingPanel({ title = "Targeting", defaultOpen = true }) {
  const [targetPresetId, setTargetPresetId] = useState("global");
  const [rangeM, setRangeM] = useState(9);
  const [speedMps, setSpeedMps] = useState(8);

  const hydratingRef = useRef(false);
  const mountedRef = useRef(false);

  function broadcastOverlayConfig(presetId = targetPresetId, r = rangeM) {
    const id = presetId;
    if (id === "global") {
      emit("EmitterTool:UI:EmitterOverlayConfig", { show: false });
      return;
    }
    if (id === "direction") {
      const spreadDeg = 60;
      emit("EmitterTool:UI:EmitterOverlayConfig", {
        show: true,
        shape: "cone",
        settings: { radius: Number(r) || 0, spreadDeg },
      });
      return;
    }
    emit("EmitterTool:UI:EmitterOverlayConfig", {
      show: true,
      shape: "line",
      settings: { width: Number(r) || 0, height: 0 },
    });
  }

  useEffect(() => {
    if (!mountedRef.current || hydratingRef.current) return;
    const preset = byId[targetPresetId] || TARGET_PRESETS[0];
    emit("EmitterTool:UI:TargetingPreset", {
      presetId: preset.id,
      spell: {
        id: `ui_${preset.id}`,
        name: preset.label,
        trigger: preset.spell.trigger,
        targeting: preset.spell.targeting,
      },
    });
    broadcastOverlayConfig(preset.id, rangeM);
  }, [targetPresetId]);

  const onRangeChange = (next) => {
    const m = Math.max(0, toNum(next, rangeM));
    setRangeM(m);
    emit("EmitterTool:UI:GridConfigChange", { config: {} });
    emit("EmitterTool:Targeting:SetRangeM", { rangeM: m });
    broadcastOverlayConfig(targetPresetId, m);
  };

  const onSpeedChange = (next) => {
    const mps = Math.max(0, toNum(next, speedMps));
    setSpeedMps(mps);
    emit("EmitterTool:UI:TokenToTargetConfig", { headSpeedMps: mps });
  };

  const hydrateState = (modeId, r, v) => {
    hydratingRef.current = true;
    try {
      const nextMode = modeId || targetPresetId;
      if (nextMode && nextMode !== targetPresetId) setTargetPresetId(nextMode);
      if (Number.isFinite(+r)) setRangeM(Number(r));
      if (Number.isFinite(+v)) setSpeedMps(Number(v));
      setTimeout(() => {
        broadcastOverlayConfig(nextMode || targetPresetId, Number.isFinite(+r) ? Number(r) : rangeM);
      }, 0);
    } finally {
      setTimeout(() => {
        hydratingRef.current = false;
      }, 0);
    }
  };

  useEffect(() => {
    let lsMode = "global";
    let lsRange = undefined;
    let lsSpeed = undefined;
    try {
      const m = localStorage.getItem("EmitterTool:targeting:mode");
      if (m) lsMode = m;
    } catch {}
    try {
      const r = Number(localStorage.getItem("EmitterTool:targeting:rangeM"));
      if (Number.isFinite(r)) lsRange = r;
    } catch {}
    try {
      const s = Number(localStorage.getItem("EmitterTool:targeting:headSpeedMps"));
      if (Number.isFinite(s)) lsSpeed = s;
    } catch {}

    const modeId = normalizeModeId(lsMode) || "global";
    hydrateState(modeId, lsRange, lsSpeed);
    mountedRef.current = true;
  }, []);

  useEffect(() => {
    const onHydrate = (e) => {
      const t = e?.detail || {};
      const mode =
        (t.mode || t.targeting?.mode || t.targetingMode || "").toString();
      const modeId = normalizeModeId(mode) || null;
      const r = Number.isFinite(+t.rangeM) ? Number(t.rangeM) : undefined;
      const v =
        Number.isFinite(+t.headSpeedMps) ? Number(t.headSpeedMps)
        : Number.isFinite(+t.speedMps) ? Number(t.speedMps)
        : undefined;
      hydrateState(modeId, r, v);
    };

    const onPresetApplied = (e) => {
      const preset = e?.detail?.preset;
      if (preset && preset.TargetingPanel && typeof preset.TargetingPanel === "object") {
        const tp = preset.TargetingPanel;
        const modeId = normalizeModeId(tp.presetId || tp.mode) || null;
        const r = Number.isFinite(+tp.rangeM) ? Number(tp.rangeM) : undefined;
        const v = Number.isFinite(+tp.headSpeedMps) ? Number(tp.headSpeedMps) : undefined;
        hydrateState(modeId, r, v);
      } else {
        let lsMode = null, lsRange, lsSpeed;
        try { lsMode = localStorage.getItem("EmitterTool:targeting:mode"); } catch {}
        try {
          const r = Number(localStorage.getItem("EmitterTool:targeting:rangeM"));
          if (Number.isFinite(r)) lsRange = r;
        } catch {}
        try {
          const s = Number(localStorage.getItem("EmitterTool:targeting:headSpeedMps"));
          if (Number.isFinite(s)) lsSpeed = s;
        } catch {}
        const modeId = normalizeModeId(lsMode);
        hydrateState(modeId, lsRange, lsSpeed);
      }
    };

    const onOverlayRequest = () => {
      broadcastOverlayConfig();
    };

    window.addEventListener("EmitterTool:Targeting:Hydrate", onHydrate);
    window.addEventListener("EmitterTool:UI:PresetApplied", onPresetApplied);
    window.addEventListener("EmitterTool:UI:RequestEmitterOverlayConfig", onOverlayRequest);
    return () => {
      window.removeEventListener("EmitterTool:Targeting:Hydrate", onHydrate);
      window.removeEventListener("EmitterTool:UI:PresetApplied", onPresetApplied);
      window.removeEventListener("EmitterTool:UI:RequestEmitterOverlayConfig", onOverlayRequest);
    };
  }, [targetPresetId, rangeM]);

  const isTokenToTarget = targetPresetId === "tokentotarget";
  const isGlobal = targetPresetId === "global";

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 2 }}>
        <div style={label}>Targeting</div>
        <select
          value={targetPresetId}
          onChange={(e) => setTargetPresetId(e.target.value)}
          style={{
            background: "#0b0d12",
            color: "white",
            border: "1px solid #374151",
            borderRadius: 6,
            padding: "6px 8px",
            fontSize: 12,
            width: "100%",
          }}
        >
          {TARGET_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        {isGlobal && (
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6, lineHeight: 1.4 }}>
            Ambient mode: the runtime runs continuously in <strong>Infinite</strong> mode.
            Effects don’t wait for clicks or token selection (ideal for rain, snow, fog).
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 12,
          border: "1px solid #1f2937",
          borderRadius: 8,
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          opacity: isGlobal ? 0.5 : 1,
        }}
      >
        <div style={{ ...label, fontWeight: 600 }}>Range</div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>How long the guide line is (metres).</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="range"
            min={0}
            max={60}
            step={0.5}
            value={rangeM}
            onChange={(e) => onRangeChange(e.target.value)}
            style={{ flex: 1 }}
            disabled={isGlobal}
          />
          <input
            type="number"
            value={rangeM}
            step={0.5}
            min={0}
            max={999}
            onChange={(e) => onRangeChange(e.target.value)}
            style={numInputStyle}
            disabled={isGlobal}
          />
          <div style={{ fontSize: 12, opacity: 0.8 }}>m</div>
        </div>
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          Current: <strong>{Number(rangeM).toFixed(1)} m</strong>
        </div>
      </div>

      {isTokenToTarget && (
        <div
          style={{
            marginTop: 12,
            border: "1px solid #1f2937",
            borderRadius: 8,
            padding: 10,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ ...label, fontWeight: 600 }}>Launch Speed</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            How fast the <em>emitter head</em> travels to the target (metres/second).
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="range"
              min={0}
              max={60}
              step={0.5}
              value={speedMps}
              onChange={(e) => onSpeedChange(e.target.value)}
              style={{ flex: 1 }}
            />
            <input
              type="number"
              value={speedMps}
              step={0.5}
              min={0}
              max={999}
              onChange={(e) => onSpeedChange(e.target.value)}
              style={numInputStyle}
            />
            <div style={{ fontSize: 12, opacity: 0.8 }}>m/s</div>
          </div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>
            Current: <strong>{Number(speedMps).toFixed(1)} m/s</strong>
          </div>
        </div>
      )}
    </CollapsibleBox>
  );
}

export { TargetingPanel };
